import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns';
import { isArray, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { DateRangePicker } from 'src/components/DateRangePicker';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Package } from 'src/models/package/_types';
import { fetchPackageOptions } from 'src/models/package/package';
import { getUpcomingSessionShortcuts } from 'src/shared/Filters/upcomingSessionDateRange';
import { Identifier } from 'src/types/utilityTypes';
import { dateToTimestamp, timestampToDate } from 'src/utils/fieldNormalizers';

import { LocationSelect } from '../Forms/Selects/LocationSelect';
import { ServiceSelect } from '../Forms/Selects/ServiceSelect';

import { LastCompletedVisitFilterFields } from './clientVisitFilters/LastCompletedVisitFilterFields';
import { VisitCountFilterFields } from './clientVisitFilters/VisitCountFilterFields';
import {
  LastCompletedVisitFilterValue,
  VisitCountFilterValue,
} from './clientVisitFilters/_types';
import { useClientVisitFilterLabels } from './clientVisitFilters/useClientVisitFilterLabels';

/*
  This filter has a lot of options and special cases.
  Backend endpoints expect 'q[client_filter]' to be a 5-tuple
  [value, (number | packageId | locationId), dateStart, dateEnd, trainerId]

  The structure of the Visit Filter parameter is as follows:
  [value, operator, timeframe, count, trainerId]
*/

export interface ClientCustomStatusFilterValueItem {
  // The main "value" for this option
  value: string;
  // `needsNumber` will set the input number
  // `needsPackage` will set the package id as number
  // `needsLocation` will set the location id as number
  // `needsService` will set the service id as number
  // Only one of these options can be used at a time
  number?: string;
  // `needsDateRange` will set start & end.
  // The backend expects them even they arent used, so we send default values
  start: string;
  end: string;
  visitValue?: VisitCountFilterValue;   // The visit count filter value
}
export interface ClientCustomStatusFilterValue {
  values?: ClientCustomStatusFilterValueItem[];
}

export const serializeCustomStatusFilter = (values: ClientCustomStatusFilterValueItem[], trainerId?: Identifier): string[] => {
  if (isEmpty(values)) return [];
  const results: string[] = [];
  for (const item of values) {
    if (item.value?.startsWith('visit_')) {
      if (item.visitValue?.operator) {
        results.push(
          `${item.value || ''},${item.visitValue?.operator || ''},${item.visitValue?.timeframeDays || ''},${item.visitValue?.value ?? ''},${trainerId || ''}`
        );
      }
    } else {
      results.push(
        `${item.value || ''},${item.number || ''},${item.start || ''},${item.end || ''},${trainerId || ''}`
      );
    }
  }
  return results;
}

interface ClientCustomStatusFilterProps {
  field: string;
  label: string;
  value: any;
  setValue: (newVal: ClientCustomStatusFilterValue | null) => void;
  valueOnly?: boolean;
}

type ClientVisitFilterField = "completedVisitCount" | "cancelledVisitCount" | "noShowVisitCount" | "upcomingVisitCount" | "lastCompletedVisit";

interface OptionConfig {
  label: string;
  val: string | null;
  description?: string;
  tooltip?: string;
  needsDateRange?: boolean;
  needsNumber?: boolean;
  numberLabel?: string;
  needsPackage?: boolean;
  needsLocation?: boolean;
  needsService?: boolean;
  visitField?: ClientVisitFilterField;
}

const customFilterOptions = [
  { label: 'Any', val: null },
  { label: 'Logged a Workout', val: 'logged_workout' },
  { label: 'No Logged Workout', val: 'no_workouts' },
  {
    label: 'No Recently Logged Workout',
    val: 'not_logged_workout',
    description: 'Not logged a workout during this time frame.',
    needsDateRange: true,
  },
  {
    label: 'Has Unsigned Waiver',
    val: 'has_unsigned_waiver',
    description: 'Has an unsigned waiver.',
  },
  { label: `Plan Assigned`, val: 'plan_assigned' },
  {
    label: `No Plan Assigned`,
    val: 'no_plan_assigned',
  },
  {
    label: `Scheduled on a Plan`,
    val: 'scheduled_plan',
  },
  {
    label: `Week Plan Ending Soon`,
    val: 'ending_soon',
    description: 'Has 7 or fewer workouts left on week plan.',
  },
  {
    label: `Calendar Plan Ending Soon`,
    val: 'calendar_plan_ending',
    description: `A Plan on their calendar ending during this time frame.`,
    needsDateRange: true,
  },
  {
    label: `Calendar Ending Soon`,
    val: 'calendar_ending',
    description: `The last workout in the calendar is within this time frame.`,
    needsDateRange: true,
  },
  {
    label: `Assessment Assigned`,
    val: 'assessment_sent',
  },
  {
    label: `Assessment Completed`,
    val: 'assessment_completed',
  },
  {
    label: `Assessment Completed Recently`,
    val: 'completed_assessment',
    description: `Completed an assessment in the time frame.`,
    needsDateRange: true,
  },
  { label: 'Failed Payments', val: 'failed_payment' },
  {
    label: 'Recently Failed Payments',
    val: 'failed_payments',
    description: 'Payment Failed during this time frame.',
    needsDateRange: true,
  },
  {
    label: 'Subscription Contract Ending Soon',
    val: 'subscription_ending_soon',
    needsNumber: true,
    numberLabel: 'Payments Left',
    description: 'Less than or equal to',
  },
  {
    label: 'Card Expiring Soon',
    val: 'expiring_card',
    description: 'Credit card is expiring during this time frame.',
    needsDateRange: true,
  },
  {
    label: `Package Expiring Soon`,
    val: 'expiring_package',
    description: `Package is expiring during this time frame.`,
    needsDateRange: true,
    needsPackage: true,
  },
  {
    label: `Owns This Package`,
    val: 'owned_package',
    needsPackage: true,
  },
  {
    label: `Managers With Dependant That Owns This Package`,
    val: 'dependent_owns_packages',
    needsPackage: true,
  },
  {
    label: `Owns This Location`,
    val: 'owned_location',
    needsLocation: true,
  },
  {
    label: 'Has Upcoming Session',
    val: 'has_upcoming_session',
    description: 'Has at least one session booked in this time frame.',
    needsDateRange: true,
  },
  {
    label: 'Has Visited This Service',
    val: 'owned_service',
    needsService: true,
  },
  {
    label: 'No Recent Visits',
    val: 'no_visit',
    description: 'Has not had a session during this time frame.',
    needsDateRange: true,
  },
  { label: 'Birthday', val: 'birthday_today', needsDateRange: true },
  {
    label: 'Signup Yearly Anniversary',
    val: 'signup_date',
    needsNumber: true,
    numberLabel: 'Years',
    needsDateRange: true,
    tooltip:
      'Enter a number. only users who have been signed up for that many years in the range specified below will show up',
  },
  {
    label: 'Signup Month Milestone',
    val: 'signup_date_month',
    needsNumber: true,
    numberLabel: 'Months',
    needsDateRange: true,
    tooltip:
      'Enter a number. only users who have been signed up for that many months in the range specified below will show up',
  },
  {
    label: `New Client`,
    val: 'new_clients',
    description: `New client during this time frame.`,
    needsDateRange: true,
  },
  { label: 'Current In-App Purchase Subscription', val: 'in_app_purchase' },
  { label: 'Payment Method on File', val: 'has_payment_method' },
  { label: 'No Payment Method on File', val: 'has_no_payment_method' },
  { label: 'Credit Card on File', val: 'credit_card_on_file' },
  { label: 'Bank Account on File', val: 'bank_acc_on_file' },
  { label: 'Paused Subscription', val: 'has_paused_subscription' },
  { label: 'Negative Service Balance', val: 'negative_service_balance' },
  { label: 'Positive Service Balance', val: 'positive_service_balance' },
  { label: 'Any Service Balance', val: 'any_service_balance' },
  { label: 'Positive Account Balance', val: 'positive_account_balance' },
  { label: 'Negative Account Balance', val: 'negative_account_balance' },
  { label: 'Any Account Balance', val: 'any_account_balance' },
  { label: `Is Dependent`, val: 'has_dependents' },
  { label: `Is Manager`, val: 'has_managers' },
  { label: `New Messages`, val: 'new_messages', needsDateRange: true },
  {
    label: 'Cancelled visit count',
    val: 'visit_cancelled_visit_count',
    visitField: 'cancelledVisitCount',
  },
  {
    label: 'Completed visit count',
    val: 'visit_completed_visit_count',
    visitField: 'completedVisitCount',
  },
  {
    label: 'Last completed visit',
    val: 'visit_last_completed_visit',
    visitField: 'lastCompletedVisit',
  },
  {
    label: 'No-show visit count',
    val: 'visit_no_show_visit_count',
    visitField: 'noShowVisitCount',
  },
  {
    label: 'Upcoming visits count',
    val: 'visit_upcoming_visit_count',
    visitField: 'upcomingVisitCount',
  },
].sort((a, b) => a.label.localeCompare(b.label)) as OptionConfig[];

const today = new Date();
const defaultStart = subDays(today, today.getDay() + 7);
const defaultEnd = today;
const UPCOMING_SESSION_VALUE = 'has_upcoming_session';

const getDefaultDateRange = (filterValue: string | null) => {
  if (filterValue === UPCOMING_SESSION_VALUE) {
    return {
      start: startOfDay(today),
      end: endOfDay(addDays(today, 30)),
    };
  }

  return { start: defaultStart, end: defaultEnd };
};

export const ClientCustomStatusFilter = ({
  label,
  setValue,
  value,
  valueOnly,
}: ClientCustomStatusFilterProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'clients.page' });
  const { t: visitFilterT } = useTranslation('translation', {
    keyPrefix: 'clients.page.visitFilters',
  });
  const {
    completedTimeframes,
    lastCompletedTimeframes,
    pastTimeframes,
    upcomingTimeframes,
  } = useClientVisitFilterLabels();

  const isBlankValue = !isArray(value?.values) || value?.values.length === 0;
  const [selectedOptions, setSelectedOptions] = useState<OptionConfig[]>([]);
  useEffect(() => {
    if (isBlankValue) {
      setSelectedOptions([]);
      return;
    }
    const newSelectedOptions: OptionConfig[] = [];
    for (const item of value.values) {
      const matchingOption = customFilterOptions.find(
        (option) => option.val === item.value
      );
      if (matchingOption) {
        newSelectedOptions.push(matchingOption);
      }
    }
    setSelectedOptions(newSelectedOptions);
  }, [value, isBlankValue]);

  const getNumber = (option: OptionConfig) => {
    if (isBlankValue) {
      return;
    }
    for (const item of value.values) {
      if (item.value === option.val) {
        return item.number;
      }
    }
  }
  const setNumber = (option: OptionConfig, newNumber: string) => {
    const newFilterValue: ClientCustomStatusFilterValue = {
      values: [...(value?.values || [])],
    }
    for (const item of newFilterValue.values!) {
      if (item.value === option.val) {
        item.number = newNumber;
      }
    }
    setValue(newFilterValue);
  };

  const getDateRange = (option: OptionConfig): [Date, Date] => {
    const defaultDateRange = getDefaultDateRange(option?.val || null);

    if (isBlankValue) {
      return [defaultDateRange.start, defaultDateRange.end];
    }
    for (const item of value.values) {
      if (item.value === option.val) {
        return [
          item?.start ? timestampToDate(item?.start) : defaultDateRange.start,
          item?.end ? timestampToDate(item?.end) : defaultDateRange.end,
        ]
      }
    }
    return [defaultDateRange.start, defaultDateRange.end];
  }
  const setDateRange = (option: OptionConfig, newRange: [Date, Date]) => {
    const newFilterValue: ClientCustomStatusFilterValue = {
      values: [...(value?.values || [])],
    }
    for (const item of newFilterValue.values!) {
      if (item.value === option.val) {
        item.start = String(dateToTimestamp(newRange[0]));
        item.end = String(dateToTimestamp(newRange[1]));
      }
    }
    setValue(newFilterValue);
  }

  const handleOptionChange = (newValues: OptionConfig[]) => {
    if (!newValues.length) {
      setValue(null);
      setSelectedOptions(newValues);
      return;
    }

    const willRemoveItems: OptionConfig[] = [];
    const willAddItems: OptionConfig[] = [];
    newValues.forEach((option) => {
      if (!selectedOptions.find((o) => o.val === option.val)) {
        willAddItems.push(option);
      }
    });
    selectedOptions.forEach((option) => {
      if (!newValues.find((o) => o.val === option.val)) {
        willRemoveItems.push(option);
      }
    });

    const newFilterValue: ClientCustomStatusFilterValue = {
      values: value?.values?.filter((item) => !willRemoveItems.find(option => option.val === item.value)) || [],
    };
    willAddItems.forEach((option) => {
      const defaultDateRange = getDefaultDateRange(option?.val || null);
      const newItem: ClientCustomStatusFilterValueItem = {
        value: option.val as string,
        start: String(dateToTimestamp(defaultDateRange.start)),
        end: String(dateToTimestamp(defaultDateRange.end)),
      };
      if (option.needsNumber) { newItem.number = ''; }
      if (option.visitField) {
        newItem.start = '';
        newItem.end = '';
        if (option.visitField === 'completedVisitCount') {
          newItem.visitValue = { operator: 'gt', value: 0, timeframeDays: 7 }
        }
        if (option.visitField === 'cancelledVisitCount') {
          newItem.visitValue = { operator: 'gt', value: 0, timeframeDays: 7 }
        }
        if (option.visitField === 'noShowVisitCount') {
          newItem.visitValue = { operator: 'gt', value: 0, timeframeDays: 7 }
        }
        if (option.visitField === 'upcomingVisitCount') {
          newItem.visitValue = { operator: 'gt', value: 0, timeframeDays: 7 }
        }
        if (option.visitField === 'lastCompletedVisit') {
          newItem.visitValue = { operator: 'gt', timeframeDays: 7 }
        }
      }
      newFilterValue.values!.push(newItem);
    });

    setValue(newFilterValue);
    setSelectedOptions(newValues);
  }
  const getVisitFilterValue = (option: OptionConfig): VisitCountFilterValue|undefined => {
    if (isBlankValue) {
      return;
    }
    for (const item of value.values) {
      if (item.value === option.val && option.visitField) {
        return item.visitValue
      }
    }
  }
  const setVisitFilterValue = (option: OptionConfig, visitValue: VisitCountFilterValue|null) => {
    const newFilterValue: ClientCustomStatusFilterValue = {
      values: [...value.values],
    };
    for (const item of newFilterValue.values!) {
      if (item.value === option.val && option.visitField) {
        item.visitValue = visitValue!;
      }
    }
    setValue(newFilterValue);
  };

  const removeOption = (option: OptionConfig) => {
    handleOptionChange(
      selectedOptions.filter((selected) => selected.val !== option.val)
    );
  };

  const availableOptions = customFilterOptions.filter(
    (option) =>
      option.val !== null &&
      !selectedOptions.some((selected) => selected.val === option.val)
  );

  const renderOptionFields = (option: OptionConfig) => (
    <>
      {!!option?.needsNumber && (
        <TextField
          type="number"
          value={getNumber(option)}
          label={option?.numberLabel || 'Number'}
          onChange={(e) => {
            setNumber(option, e.target.value);
          }}
        />
      )}

      {!!option?.needsPackage && (
        <PackageSelect
          value={getNumber(option)}
          setValue={(newNumber) => {
            setNumber(option, newNumber);
          }}
        />
      )}

      {!!option?.needsLocation && (
        <LocationSelect
          value={getNumber(option)}
          onChange={(newValue) => setNumber(option, newValue)}
          label="Location"
          includeNoLocation
        />
      )}

      {!!option?.needsService && (
        <ServiceSelect
          value={getNumber(option)}
          onChange={(newValue) => setNumber(option, newValue)}
          label="Service"
        />
      )}

      {!!option?.tooltip && (
        <Typography color="text.secondary" variant="body2">
          {option.tooltip}
        </Typography>
      )}

      {!!option?.needsDateRange && (
        <DateRangePicker
          multiInput
          value={getDateRange(option)}
          onChange={(newValue) => {
            if (newValue[0] && newValue[1]) {
              const start =
                option?.val === UPCOMING_SESSION_VALUE
                  ? startOfDay(newValue[0])
                  : newValue[0];
              const end =
                option?.val === UPCOMING_SESSION_VALUE
                  ? endOfDay(newValue[1])
                  : newValue[1];
              setDateRange(option, [start, end]);
            }
          }}
          shortcutItems={
            option?.val === UPCOMING_SESSION_VALUE
              ? getUpcomingSessionShortcuts()
              : undefined
          }
          minDate={
            option?.val === UPCOMING_SESSION_VALUE
              ? today
              : undefined
          }
          label=""
        />
      )}

      {!!option?.description && (
        <Typography color="text.secondary" variant="body2">
          {option.description}
        </Typography>
      )}

      {option.visitField === 'completedVisitCount' && (
        <VisitCountFilterFields
          value={getVisitFilterValue(option)}
          onChange={(nextValue) => setVisitFilterValue(option, nextValue)}
          timeframeOptions={completedTimeframes}
          direction="past"
          operatorLabel={visitFilterT('label.operator.completed')}
          countLabel={visitFilterT('label.visitCount.completed')}
        />
      )}

      {option.visitField === 'cancelledVisitCount' && (
        <VisitCountFilterFields
          value={getVisitFilterValue(option)}
          onChange={(nextValue) => setVisitFilterValue(option, nextValue)}
          timeframeOptions={pastTimeframes}
          direction="past"
          operatorLabel={visitFilterT('label.operator.cancelled')}
          countLabel={visitFilterT('label.visitCount.cancelled')}
        />
      )}

      {option.visitField === 'noShowVisitCount' && (
        <VisitCountFilterFields
          value={getVisitFilterValue(option)}
          onChange={(nextValue) => setVisitFilterValue(option, nextValue)}
          timeframeOptions={pastTimeframes}
          direction="past"
          operatorLabel={visitFilterT('label.operator.noShow')}
          countLabel={visitFilterT('label.visitCount.noShow')}
        />
      )}

      {option.visitField === 'upcomingVisitCount' && (
        <VisitCountFilterFields
          value={getVisitFilterValue(option)}
          onChange={(nextValue) => setVisitFilterValue(option, nextValue)}
          timeframeOptions={upcomingTimeframes}
          direction="future"
          operatorLabel={visitFilterT('label.operator.upcoming')}
          countLabel={visitFilterT('label.visitCount.upcoming')}
        />
      )}

      {option.visitField === 'lastCompletedVisit' && (
        <LastCompletedVisitFilterFields
          value={getVisitFilterValue(option) as LastCompletedVisitFilterValue}
          onChange={(nextValue) => setVisitFilterValue(option, nextValue)}
          timeframeOptions={lastCompletedTimeframes}
        />
      )}
    </>
  );

  if (valueOnly) {
    if (selectedOptions.length === 0) {
      return <>{label}</>;
    }
    if (selectedOptions.length === 1) {
      return <>{selectedOptions[0].label}</>;
    }

    return (
      <>
        {t('customStatusFilter.multipleSelected', {
          count: selectedOptions.length,
        })}
      </>
    );
  }

  return (
    <Stack spacing={0} sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {label}
      </Typography>

      {selectedOptions.map((option, index) => (
        <Box key={option.val}>
          {index > 0 && <Divider sx={{ my: 2, opacity: 0.5 }} />}
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {option.label}
              </Typography>
              <IconButton
                size="small"
                color="info"
                onClick={() => removeOption(option)}
                aria-label={t('customStatusFilter.removeStatus', {
                  status: option.label,
                })}
              >
                <Icon icon={faXmark} size="small" />
              </IconButton>
            </Stack>

            <Stack spacing={1.5}>{renderOptionFields(option)}</Stack>
          </Stack>
        </Box>
      ))}

      {selectedOptions.length > 0 && (
        <Divider sx={{ my: 2, opacity: 0.5 }} />
      )}

      <Autocomplete
        options={availableOptions}
        getOptionLabel={(option) => option.label}
        value={null}
        disabled={availableOptions.length === 0}
        onChange={(_, newValue) => {
          if (newValue) {
            handleOptionChange([...selectedOptions, newValue]);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('customStatusFilter.addStatus')}
          />
        )}
        renderOption={(props, option) => {
          return (
            <ListItem
              {...(props as any)}
              clickable
              primary={option.label}
              key={option.val ?? 'any'}
            />
          );
        }}
      />
    </Stack>
  );
};

const ANY_PACKAGE_OPTION = { id: -2, name: 'Any Package' } as Package;

interface PackageSelectProps {
  value: any;
  setValue: (newVal: any) => void;
}
export const PackageSelect: React.FC<PackageSelectProps> = ({
  value,
  setValue,
}) => {
  const { data: packages, isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => {
      return fetchPackageOptions();
    },
    staleTime: 60000,
  });

  const options = packages ? [ANY_PACKAGE_OPTION, ...packages] : [ANY_PACKAGE_OPTION];

  const selectedPackage = options?.find((group) => group.id === value) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(pkg: Package) => pkg?.name || 'Loading'}
      value={selectedPackage}
      onChange={(_, newValue: Package) => setValue(newValue?.id)}
      loading={isLoading}
      renderInput={(params) => (
        <TextField {...params} label={'Package'} placeholder="Package" />
      )}
      renderOption={(props, group) => {
        return (
          <ListItem
            {...(props as any)}
            clickable
            primary={group.name}
            key={group.name + group.id}
          />
        );
      }}
    />
  );
};
